// Procedural Web Audio SFX. Tiny, deterministic, zero asset bytes, mutable, and NEVER the
// sole signal for any state (R8). AudioContext is created lazily on first user gesture
// (autoplay policy). Sound is OFF-by-mute persisted and only enhances, never gates.

import { loadSetting, saveSetting } from './store.js';

let ctx: AudioContext | null = null;
let muted = loadSetting('sound-muted', false);

function ac(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(value: boolean): void {
  muted = value;
  saveSetting('sound-muted', value);
}

export function toggleMuted(): boolean {
  setMuted(!muted);
  return muted;
}

interface ToneSpec {
  freq: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  glideTo?: number;
  delay?: number;
}

function tone({ freq, dur, type = 'sine', gain = 0.18, glideTo, delay = 0 }: ToneSpec): void {
  const a = ac();
  if (!a || muted) return;
  const t0 = a.currentTime + delay;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
  // Soft attack + exponential decay envelope (no clicks).
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(a.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** Soft focus/hover blip. */
export function tick(): void {
  tone({ freq: 520, dur: 0.05, type: 'triangle', gain: 0.08 });
}

/** Gentle "not that one" — a low, brief, non-punishing shimmer. */
export function nope(): void {
  tone({ freq: 240, dur: 0.16, type: 'sine', gain: 0.12, glideTo: 190 });
}

/** The aha reveal chime — a clean ascending arpeggio (warm major triad). */
export function reveal(): void {
  tone({ freq: 523.25, dur: 0.5, type: 'sine', gain: 0.16 });
  tone({ freq: 659.25, dur: 0.5, type: 'sine', gain: 0.14, delay: 0.09 });
  tone({ freq: 783.99, dur: 0.6, type: 'sine', gain: 0.13, delay: 0.18 });
  tone({ freq: 1046.5, dur: 0.7, type: 'triangle', gain: 0.09, delay: 0.27 });
}

/** A soft pick/confirm. */
export function pickSfx(): void {
  tone({ freq: 660, dur: 0.09, type: 'triangle', gain: 0.12, glideTo: 880 });
}

/** Prime the audio context on a user gesture (call from the first interaction). */
export function primeAudio(): void {
  ac();
}
